import { inject, NgModule } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  NavigationEnd,
  Router,
  RouterModule,
  Routes,
} from '@angular/router';
import { BrowserUtils } from '@azure/msal-browser';
import { AdminOpcosUersDataAccessService } from '@flexyoo/admin/opcos/data-access';
import { TenantGroupsComponent } from '@flexyoo/admin/tenant-groups/ui';
import { ADMIN_USERS_LIST_DATA_ACCESS } from '@flexyoo/admin/users/models';
import {
  ADMIN_USER_FORM_DIALOG,
  AdminOpcosFormDialogService,
  UserListFilterOptions,
  USERS_LIST_FILTER_OPTIONS,
} from '@flexyoo/admin/users/ui';
import { TenantApiService } from '@flexyoo/api/services';
import { environment } from '@flexyoo/environment';
import { confirmLeaveGuard } from '@flexyoo/shared/services';
import { ChoosePasswordOutdatedPageComponent } from '@flexyoo/shared/ui';
import { TenantDictionariesActions, tenantDictionariesSelectors } from '@flexyoo/tenant/store';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AutoLoginPartialRoutesGuard } from 'angular-auth-oidc-client';
import { of, switchMap, take } from 'rxjs';
import { HrInviteEmployeeDateRangeDialogService } from 'src/app/hr/shared/hr-invite-employee-date-range/hr-invite-employee-date-range-dialog.service';
import { ChoosePasswordComponent } from './authentication/components/choose-password/choose-password.component';
import { EiffageLoginComponent } from './authentication/components/eiffage-login/eiffage-login.component';
import { ForgotPasswordComponent } from './authentication/components/forgot-password/forgot-password.component';
import { LoginComponent } from './authentication/components/login/login.component';
import { ResetPasswordComponent } from './authentication/components/reset-password/reset-password.component';
import { AdminGuard } from './authentication/services/admin-guard.service';
import { CafeteriaUserAgreementGuard } from './authentication/services/cafeteria-user-agreement-guard.service';
import { ChoosePasswordGuard } from './authentication/services/choose-password-guard.service';
import { EmployeeAccountGuard } from './authentication/services/employee-account-guard.service';
import { EmployeeGuard } from './authentication/services/employee-guard.service';
import { EmployeeMobilityBudgetGuard } from './authentication/services/employee-mobility-budget-guard.service';
import { EmployeeVandenBorreOrderGuardService } from './authentication/services/employee-vanden-borre-order-guard.service';
import { ForgotPasswordGuard } from './authentication/services/forgot-password-guard.service';
import { AuthGuard } from './authentication/services/guard.service';
import { HrCafeteriaPlanGuard } from './authentication/services/hr-cafeteria-plan-guard.service';
import { HRGuard } from './authentication/services/hr-guard.service';
import { HrMobilityBudgetPlanGuard } from './authentication/services/hr-mobility-budget-plan-guard.service';
import { LoginGuard } from './authentication/services/login-guard.service';
import { OpcoGuard } from './authentication/services/opco-guard.service';
import { UserAccountGuard } from './authentication/services/user-account-guard.service';
import { UserAgreementGuard } from './authentication/services/user-agreement-guard.service';
import { HrBenefitDetailsCreateService } from './hr/hr-benefits/hr-benefit-details-page/hr-benefit-details-create.service';
import { AuthLayoutComponent } from './layout/auth-layout.component';
import { BaseLayoutComponent } from './layout/base-layout.component';
import { MbpHrBenefitDetailsCreateService } from './libs/mobility-budget/mbp-hr-benefits/mbp-hr-benefit-details-create.service';

// declare google analytics
declare const ga: any;

const routes: Routes = [
  {
    path: '',
    component: BaseLayoutComponent,
    canActivate: [AuthGuard, UserAgreementGuard],
    children: [
      {
        path: 'admin',
        canMatch: [AdminGuard],
        children: [
          {
            path: 'parameters',
            loadComponent: () =>
              import('@flexyoo/admin/settings/ui').then((m) => m.AdminSettingsComponent),
          },
          {
            path: 'opcos',
            loadComponent: () =>
              import('@flexyoo/admin/opcos/ui').then((m) => m.AdminOpcosListComponent),
          },
          {
            path: 'users',
            loadComponent: () =>
              import('@flexyoo/admin/users/ui').then((m) => m.AdminUsersListComponent),
          },
          {
            path: 'audit-logs',
            loadComponent: () =>
              import('@flexyoo/admin/audit-log/ui').then((m) => m.AdminAuditLogComponent),
          },
          {
            path: 'olympus-admin-panel',
            loadComponent: () =>
              import('@flexyoo/admin/olympus-admin-panel/ui').then(
                (m) => m.OlympusAdminPanelComponent
              ),
          },
          {
            path: 'tools',
            loadComponent: () =>
              import('@flexyoo/admin/tools/ui').then((m) => m.BenefitsImportComponent),
          },
          {
            path: 'statistics',
            loadComponent: () =>
              import('@flexyoo/admin/tools/ui').then((m) => m.StatisticsExportComponent),
          },
          {
            path: 'reports',
            loadComponent: () =>
              import('@flexyoo/admin/reports/ui').then((m) => m.AdminReportsComponent),
          },
          {
            path: '',
            redirectTo: 'parameters',
            pathMatch: 'full',
          },
        ],
        runGuardsAndResolvers: 'always',
      },
      {
        path: 'opco',
        canMatch: [OpcoGuard],
        children: [
          {
            path: 'users',
            loadComponent: () =>
              import('@flexyoo/admin/users/ui').then((m) => m.AdminUsersListComponent),
            providers: [
              {
                provide: ADMIN_USERS_LIST_DATA_ACCESS,
                useClass: AdminOpcosUersDataAccessService,
              },
              {
                provide: ADMIN_USER_FORM_DIALOG,
                useClass: AdminOpcosFormDialogService,
              },
              {
                provide: USERS_LIST_FILTER_OPTIONS,
                useValue: {
                  useOpco: false,
                } as UserListFilterOptions,
              },
            ],
          },
          {
            path: 'clients',
            children: [
              {
                path: 'create',
                loadComponent: () =>
                  import('@flexyoo/admin/opcos/ui').then(
                    (m) => m.AdminOpcosTenantCustomerFormComponent
                  ),
              },
              {
                path: ':id',
                loadComponent: () =>
                  import('@flexyoo/admin/opcos/ui').then((m) => m.AdminOpcosTenantComponent),
                resolve: {
                  item: (route: ActivatedRouteSnapshot) =>
                    inject(TenantApiService).get(+route.paramMap.get('id')),
                },
              },
              {
                path: '',
                loadComponent: () =>
                  import('@flexyoo/admin/clients/ui').then((m) => m.AdminClientsListComponent),
              },
            ],
          },
          {
            path: 'groups',
            component: TenantGroupsComponent,
          },
          {
            path: '',
            redirectTo: 'clients',
            pathMatch: 'full',
          },
        ],
        runGuardsAndResolvers: 'always',
      },
      {
        path: 'hr',
        canMatch: [HRGuard],
        providers: [HrInviteEmployeeDateRangeDialogService],
        resolve: {
          dictionaries: () => {
            const store = inject(Store);
            const actions = inject(Actions);
            return store.select(tenantDictionariesSelectors.selectDictionaries).pipe(
              take(1),
              switchMap((dictionaries) => {
                if (Array.isArray(dictionaries?.periods) && Array.isArray(dictionaries?.years)) {
                  return of(dictionaries);
                }

                store.dispatch(TenantDictionariesActions.loadDictionaries());

                return actions.pipe(
                  ofType(
                    TenantDictionariesActions.loadDictionariesSuccess,
                    TenantDictionariesActions.loadDictionariesFailure
                  ),
                  take(1),
                  switchMap(() => store.select(tenantDictionariesSelectors.selectDictionaries)),
                  take(1)
                );
              })
            );
          },
        },
        children: [
          {
            path: 'dashboard',
            loadComponent: () => import('@flexyoo/hr/ui').then((m) => m.HrDashboardComponent),
          },
          {
            path: 'dashboard/selected-benefits',
            canActivate: [HrCafeteriaPlanGuard],
            loadComponent: () =>
              import('@flexyoo/hr/ui').then((m) => m.HrDashboardSelectedBenefitsComponent),
          },
          {
            path: 'notifications-center',
            loadComponent: () =>
              import('@flexyoo/notifications-center/components').then(
                (m) => m.NotificationsCenterComponent
              ),
          },
          {
            path: 'employee',
            loadComponent: () =>
              import('./hr/hr-employees/hr-employees.component').then(
                (m) => m.HREmployeesComponent
              ),
          },
          {
            path: 'employee/:id',
            loadComponent: () =>
              import('./hr/hr-employees/hr-employees.component').then(
                (m) => m.HREmployeesComponent
              ),
          },
          {
            path: 'employees',
            loadComponent: () =>
              import('./hr/hr-employee-list/hr-employee-list.component').then(
                (m) => m.HrEmployeeListComponent
              ),
          },
          {
            path: 'budgets-management',
            canActivate: [HrCafeteriaPlanGuard],
            loadComponent: () =>
              import('./hr/hr-employees-budget/hr-employees-budget.component').then(
                (m) => m.HrEmployeesBudgetComponent
              ),
          },
          {
            path: 'mbp',
            canActivate: [HrMobilityBudgetPlanGuard],
            canActivateChild: [HrMobilityBudgetPlanGuard],
            children: [
              {
                path: 'benefits',
                providers: [MbpHrBenefitDetailsCreateService],
                loadChildren: () =>
                  import('./libs/mobility-budget/mbp-hr-benefits/mbp-hr-benefits.routes').then(
                    (m) => m.MBP_HR_BENEFITS_ROUTES
                  ),
              },
              {
                path: 'benefits/:id',
                loadComponent: () =>
                  import('@flexyoo/mbp/components').then((m) => m.MbpHrBenefitPageComponent),
              },
              {
                path: 'benefits/:id/new',
                loadComponent: () =>
                  import('@flexyoo/mbp/components').then((m) => m.MbpHrBenefitPageComponent),
              },
              {
                path: 'budgets-management',
                loadComponent: () =>
                  import('@flexyoo/mbp/components').then((m) => m.MbpBudgetsComponent),
              },
              {
                path: 'transactions',
                loadComponent: () =>
                  import('@flexyoo/mbp/components').then((m) => m.MbpTransactionsComponent),
              },
              {
                path: 'ownership-cost',
                loadComponent: () =>
                  import('@flexyoo/mbp/components').then((m) => m.MbpOwnershipCostComponent),
              },
              // {
              //   path: 'payroll-management',
              //   loadComponent: () =>
              //     import('@flexyoo/tenant/components').then(
              //       (m) => m.TenantPayrollManagementPageComponent
              //     ),
              //   data: {
              //     isMobilityBudgetPage: true,
              //   },
              //   children: [
              //     {
              //       path: 'payroll',
              //       loadComponent: () =>
              //         import('@flexyoo/tenant/components').then(
              //           (m) => m.TenantCashPayrollComponent
              //         ),
              //       data: {
              //         isMobilityBudgetPage: true,
              //       },
              //     },
              //     {
              //       path: 'year-closing',
              //       loadComponent: () =>
              //         import('@flexyoo/tenant/components').then(
              //           (m) => m.TenantYearClosingComponent
              //         ),
              //     },
              //     {
              //       path: '',
              //       redirectTo: 'payroll',
              //       pathMatch: 'full',
              //     },
              //   ],
              // },
            ],
          },
          {
            path: 'transactions-history',
            canActivate: [HrCafeteriaPlanGuard],
            loadComponent: () =>
              import('./hr/hr-transactions/hr-transactions.component').then(
                (m) => m.HrTransactionsComponent
              ),
          },
          {
            path: 'benefits',
            canActivate: [HrCafeteriaPlanGuard],
            canActivateChild: [HrCafeteriaPlanGuard],
            providers: [HrBenefitDetailsCreateService],
            loadChildren: () =>
              import('./hr/hr-benefits/hr-benefits.routes').then((m) => m.HR_BENEFITS_ROUTES),
          },
          {
            path: 'voting-period',
            canActivate: [HrCafeteriaPlanGuard],
            loadComponent: () =>
              import('./hr/hr-benefits/voting-period/voting-period.component').then(
                (m) => m.VotingPeriodComponent
              ),
          },
          {
            path: 'payroll-management',
            canActivate: [HrCafeteriaPlanGuard],
            canActivateChild: [HrCafeteriaPlanGuard],
            loadComponent: () =>
              import('@flexyoo/tenant/components').then(
                (m) => m.TenantPayrollManagementPageComponent
              ),
            children: [
              {
                path: 'payroll',
                loadComponent: () =>
                  import('@flexyoo/tenant/components').then(
                    (m) => m.TenantPayrollManagementComponent
                  ),
              },
              {
                path: 'cash',
                loadComponent: () =>
                  import('@flexyoo/tenant/components').then((m) => m.TenantCashPayrollComponent),
              },
              {
                path: 'year-closing',
                loadComponent: () =>
                  import('@flexyoo/tenant/components').then((m) => m.TenantYearClosingComponent),
              },
              {
                path: '',
                redirectTo: 'payroll',
                pathMatch: 'full',
              },
            ],
          },
          {
            path: '',
            redirectTo: 'employees',
            pathMatch: 'full',
          },
        ],
        runGuardsAndResolvers: 'always',
      },
      {
        path: 'employee/benefit/vandenborre',
        canMatch: [EmployeeGuard],
        canActivate: [EmployeeVandenBorreOrderGuardService],
        canDeactivate: [confirmLeaveGuard],
        loadComponent: () =>
          import('./employee/overview/employee-overview.component').then(
            (m) => m.EmployeeOverviewComponent
          ),
        runGuardsAndResolvers: 'always',
      },
      {
        path: 'employee',
        canMatch: [EmployeeGuard],
        canActivate: [CafeteriaUserAgreementGuard],
        canDeactivate: [confirmLeaveGuard],
        loadComponent: () =>
          import('./employee/overview/employee-overview.component').then(
            (m) => m.EmployeeOverviewComponent
          ),
        runGuardsAndResolvers: 'always',
      },
      {
        path: 'employee-benefits',
        canMatch: [EmployeeGuard],
        canActivate: [CafeteriaUserAgreementGuard],
        loadComponent: () =>
          import(
            './employee-benefits/employee-benefits-page/employee-benefits-page.component'
          ).then((m) => m.EmployeeBenefitsPageComponent),
        runGuardsAndResolvers: 'always',
      },
      {
        path: 'employee-budget',
        canMatch: [EmployeeGuard],
        canActivate: [CafeteriaUserAgreementGuard],
        loadComponent: () =>
          import('./employee/employee-budget-page/employee-budget-page.component').then(
            (m) => m.EmployeeBudgetPageComponent
          ),
        runGuardsAndResolvers: 'always',
      },
      {
        path: 'employee-notifications-center',
        canMatch: [EmployeeGuard],
        loadComponent: () =>
          import('@flexyoo/notifications-center/components').then(
            (m) => m.NotificationsCenterComponent
          ),
        runGuardsAndResolvers: 'always',
      },
      {
        path: 'employee-mbp',
        canMatch: [EmployeeGuard],
        canActivate: [EmployeeMobilityBudgetGuard],
        canActivateChild: [EmployeeMobilityBudgetGuard],
        runGuardsAndResolvers: 'always',
        children: [
          {
            path: '',
            redirectTo: 'overview',
            pathMatch: 'full',
          },
          {
            path: 'overview',
            loadComponent: () =>
              import('@flexyoo/mbp/components').then((m) => m.MbpEmployeeBenefitsOverviewComponent),
          },
        ],
      },
      {
        path: 'employee-account',
        canMatch: [EmployeeAccountGuard],
        loadComponent: () =>
          import('@flexyoo/employee/ui').then((m) => m.EmployeeAccountPageComponent),
        runGuardsAndResolvers: 'always',
      },
      {
        path: 'account',
        canMatch: [UserAccountGuard],
        loadComponent: () =>
          import('./authentication/components/account/account.component').then(
            (m) => m.AccountComponent
          ),
        runGuardsAndResolvers: 'always',
      },
      {
        path: '',
        redirectTo: 'admin',
        pathMatch: 'full',
      },
    ],
    runGuardsAndResolvers: 'always',
  },
  {
    path: 'login',
    component: AuthLayoutComponent,
    canActivate: [LoginGuard],
    children: [
      {
        path: '',
        component: LoginComponent,
      },
    ],
    runGuardsAndResolvers: 'always',
  },
  {
    path: 'eiffage-login',
    component: EiffageLoginComponent,
    canActivate: [AutoLoginPartialRoutesGuard],
  },
  {
    path: 'choose-password',
    component: ChoosePasswordComponent,
    canActivate: [ChoosePasswordGuard],
  },
  {
    path: 'choose-password-link-outdated',
    component: ChoosePasswordOutdatedPageComponent,
  },
  {
    path: 'forgot-password',
    component: ForgotPasswordComponent,
    canActivate: [ForgotPasswordGuard],
  },
  {
    path: 'reset-password',
    component: ResetPasswordComponent,
  },
  { path: '**', redirectTo: 'login' },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      enableTracing: false,
      onSameUrlNavigation: 'reload',
      initialNavigation:
        !BrowserUtils.isInIframe() && !BrowserUtils.isInPopup() ? 'enabledNonBlocking' : 'disabled', // Set to enabledBlocking to use Angular Universal
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {
  constructor(private router: Router) {
    if (environment.useGa) {
      // uncomment index.html scripts
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd && ga !== 'undefined' && ga) {
          ga('set', 'page', event.urlAfterRedirects);
          ga('send', 'pageview');
        }
      });
    }
  }
}
